import * as d3 from 'd3';

import BenchlingConfig from 'BenchlingConfig';
import {Part} from 'biojs/Part';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  PART_ANNOTATION_ARCHIVED_COLOR,
  PART_ANNOTATION_ARCHIVED_OPACITY,
  PART_ANNOTATION_COLOR,
  PART_ANNOTATION_OUT_OF_SYNC_COLOR,
  PART_LINK_ARROW_COLOR,
  CIRCULAR_DNA_PART_ARROW_STROKE_WIDTH,
} from 'molbio/maps/BiointelligentLinksConstants';
import CircularBiointelligentLinkAnnotationsView from 'molbio/maps/circular/BiointelligentLinkAnnotationsView';
import {
  getCircularArrowTipsPath,
  getCircularArrowBodyAndTicksPath,
} from 'molbio/maps/circular/BiointelligentLinksRenderParams';
import {NEAR_BLACK, WHITE} from 'ui-library/Colors';

/**
 * View for drawing parts on the plasmid map as annotations when overlapping parts is enabled in addition
 * to rendering part overlays on the axis (See PartsView)
 */
class CircularSequencePartAnnotationsView extends CircularBiointelligentLinkAnnotationsView<Part> {
  singularClassName = 'part-annotation';

  static initClass(): void {
    this.prototype.className = 'part-annotations';
    this.prototype.singularClassName = 'part-annotation';
    this.prototype.tagName = 'g';

    // Need to set contextMenuData to null to default to showing the parts context menu.
    // Should revisit this behavior to match other features.
    // @ts-expect-error
    this.prototype.contextMenuData = null;
  }

  override fill(d: Part): string {
    const outOfSync = !d.isInSync(this.model.polymer()._sequence());
    const isArchived = d.sequence().isArchived();
    if (outOfSync) {
      return PART_ANNOTATION_OUT_OF_SYNC_COLOR;
    } else if (isArchived) {
      return PART_ANNOTATION_ARCHIVED_COLOR;
    } else {
      return PART_ANNOTATION_COLOR;
    }
  }

  override fontColor(d: Part): string {
    return d.sequence().isArchived() ? NEAR_BLACK : WHITE;
  }

  override opacity(d: Part): number {
    return d.sequence().isArchived() ? PART_ANNOTATION_ARCHIVED_OPACITY : super.opacity(d);
  }

  override data(): Array<Part> {
    // Only show part annotations if overlapping parts is enabled, otherwise PartsView already shows labels.
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      return this.model
        .get('sequence')
        .parts()
        .getSequencePartsBetween(this.model.get('leftBP'), this.model.get('rightBP'));
    }
    return [];
  }

  clickAction(e: MouseEvent, d: Part): void {
    d.click(this.model.polymer(), e.shiftKey);
  }

  dblClickAction(e: MouseEvent, d: Part): void {
    d.dblClick(this.model.polymer(), e.shiftKey);
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:bases', () => {
      this.invalidateData();
    });
  }

  handleDrawContents(selector: d3.Selection<SVGGElement, Part, SVGGElement, unknown>): void {
    if (
      !BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS ||
      this.model.get('sequence').subtype() !== ModelTypeConstants.SEQUENCE
    ) {
      return;
    }
    selector.append('svg:path').classed('biointelligent-link-inner-details', true);
    selector.append('svg:path').classed('biointelligent-link-inner-details-with-fill', true);

    const ref = this;

    selector
      .select('.biointelligent-link-inner-details-with-fill')
      .attr('fill', PART_LINK_ARROW_COLOR)
      .attr('d', (d: Part) => {
        const start = d.start();
        const end = d.end();
        const angles = ref.getAngles(start, end);
        const dimensions = ref.layerBounds(d.get('annularLayer'));
        return getCircularArrowTipsPath(angles, dimensions, d.isRevcomp());
      });

    selector
      .select('.biointelligent-link-inner-details')
      .attr('stroke-width', CIRCULAR_DNA_PART_ARROW_STROKE_WIDTH)
      .attr('stroke', PART_LINK_ARROW_COLOR)
      .attr('fill', 'none')
      .attr('d', (d: Part) => {
        const start = d.start();
        const end = d.end();
        const angles = ref.getAngles(start, end);
        const dimensions = ref.layerBounds(d.get('annularLayer'));
        return getCircularArrowBodyAndTicksPath(angles, dimensions, d.isRevcomp());
      });
  }
}
CircularSequencePartAnnotationsView.initClass();

export default CircularSequencePartAnnotationsView;
